<template>
    <RbacDisableGuard
        v-slot="{ isDisabledByRbac }"
        :features="[RbacFeature.entityDetailsEditProperties]"
    >
        <div class="EntityDetailsPropertiesTab">
            <UnsavedChangesModal
                v-if="showCancelConfirmModal"
                @input="onCancelConfirm"
            />
            <EntityDetailsPropertiesTabToolbar
                :is-editing="isEditing"
                :loading="loading"
                :entity-active="entity.active"
                :is-disabled-by-rbac="isDisabledByRbac"
                @edit="isEditing = true"
                @cancel="onCancelClick"
                @save="onSaveClick"
            />

            <div class="EntityDetailsPropertiesTab__left">
                <EntityDetailsRiskProfilesCard
                    :entity-risk-profiles="entityRiskProfiles"
                    @profile-click="$emit('profile-click', $event)"
                />
                <EntityDetailsPropertiesTabPropertiesCard
                    :entity="entity"
                    :fields="fields"
                    :is-editing="isEditing"
                    :is-disabled-by-rbac="isDisabledByRbac"
                    :is-evident-user="isEvidentUser"
                    :save-touched="saveTouched"
                    :enum-labels="enumLabels"
                    @body-change="onBodyChange"
                />
            </div>

            <EntityDetailsPropertiesTabCustomPropertiesCard
                v-if="insuredFieldsField"
                :entity="entity"
                :insured-fields-field="insuredFieldsField"
                :is-editing="isEditing"
                :is-disabled-by-rbac="isDisabledByRbac"
                :save-touched="saveTouched"
                :enum-labels="enumLabels"
                @custom-properties-change="onCustomPropertiesChange"
            />

            <!-- Collaterals — full width below the two columns -->
            <EntityDetailsPropertiesTabCard
                v-if="customPropertyCollaterals !== null"
                class="EntityDetailsPropertiesTab__collaterals"
                title="Collaterals"
                :editing="isEditing"
                collateral
            >
                <template v-if="isEditing && !updatingEntity && !isDisabledByRbac" #titleRight>
                    <NewButton
                        variant="flat"
                        color="primary"
                        size="sm"
                        data-test-id="EntityDetailsCollateralSection__addButton"
                        class="EntityDetailsPropertiesTab__editBtn"
                        @click="$refs.collateralSection.onClickAddCollateral()"
                    >
                        <EidIcon
                            :icon-src="faPlus"
                            alt="add"
                            class="EntityDetailsPropertiesTab__editBtnIcon"
                        />
                        Add Collateral
                    </NewButton>
                </template>
                <EntityDetailsCollateralSection
                    ref="collateralSection"
                    :custom-property-collaterals="customPropertyCollaterals"
                    :collaterals="collaterals"
                    :entity-display-name="entity.displayName"
                    :updating-entity="updatingEntity"
                    :rbac-disabled="isDisabledByRbac"
                    :editing-enabled="isEditing"
                    :touched="saveTouched"
                    @input="onCollateralsInput($event)"
                    @validity-change="collateralsValid = $event"
                />
            </EntityDetailsPropertiesTabCard>

            <!-- Equipment — one full-width section per Equipment custom property.
                 `collateral` gives the card the full-bleed, scrollable body a row table needs. -->
            <EntityDetailsPropertiesTabCard
                v-for="property in customPropertiesEquipment"
                :key="property.key"
                class="EntityDetailsPropertiesTab__equipment"
                :title="property.name"
                :editing="isEditing"
                collateral
            >
                <template v-if="isEditing && !updatingEntity && !isDisabledByRbac" #titleRight>
                    <NewButton
                        variant="flat"
                        color="primary"
                        size="sm"
                        data-test-id="EntityDetailsEquipmentSection__addButton"
                        class="EntityDetailsPropertiesTab__editBtn"
                        @click="onClickAddEquipment(property.key)"
                    >
                        <EidIcon
                            :icon-src="faPlus"
                            alt="add"
                            class="EntityDetailsPropertiesTab__editBtnIcon"
                        />
                        Add Equipment
                    </NewButton>
                </template>
                <EntityDetailsEquipmentSection
                    :key="equipmentResetToken"
                    :ref="(section) => registerEquipmentSection(property.key, section)"
                    :custom-property-equipment="property"
                    :equipment="getEquipmentRows(property.key)"
                    :entity-display-name="entity.displayName"
                    :updating-entity="updatingEntity"
                    :rbac-disabled="isDisabledByRbac"
                    :editing-enabled="isEditing"
                    :touched="saveTouched"
                    @input="onEquipmentInput(property.key, $event)"
                    @validity-change="onEquipmentValidityChange(property.key, $event)"
                />
            </EntityDetailsPropertiesTabCard>

            <EntityDetailsProducersCard
                v-if="producerFlowEnabled"
                :producers="entity.producerList || []"
                :enum-labels="enumLabels"
                :is-editing="isEditing"
                :is-disabled-by-rbac="isDisabledByRbac"
                :save-touched="saveTouched"
                :rows="producerRows"
                :coverage-types="producerCoverageTypes"
                :entity-address="entityAddress"
                :wc-jurisdictions="wcJurisdictions"
                :wc-jurisdictions-failed="wcJurisdictionsFailed"
                :requirements-loaded="producerRequirementsLoaded"
                :entity-contact-email="contactEmail"
                :server-errors="producerServerErrors"
                @rows-change="onProducersChange"
                @validity-change="producersValid = $event"
                @retry-wc-jurisdictions="loadWcJurisdictions"
            />
        </div>
    </RbacDisableGuard>
</template>

<style lang="scss">
.EntityDetailsPropertiesTab {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 20px 28px 28px;
    align-items: start;

    &__left {
        display: flex;
        flex-direction: column;
        gap: 16px;
        position: relative;
        min-width: 0;
    }

    // Read ↔ edit cross-fade
    &__modeFade {
        &-enter-active,
        &-leave-active {
            transition: opacity 0.15s ease;
        }

        // PRODUCT-26110 Task 12b (TRANSITION_CLASSES): Vue 3 renamed the v2 `X-enter`
        // class to `X-enter-from` (still combined with `X-enter-active` during the
        // enter phase); `X-leave-to` is unchanged between v2 and v3.
        &-enter-from,
        &-leave-to {
            opacity: 0;
        }
    }

    &__editBtn {
        display: flex;
        align-items: center;
        gap: 6px;
        font-family: "Roboto", sans-serif;
        font-size: 12px;
        font-weight: 500;
    }

    &__editBtnIcon {
        font-size: 11px;
    }

    // -------------------------
    // READ MODE
    // -------------------------
    &__readView {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    &__readGrid {
        display: grid;
        grid-template-columns: minmax(0, 150px) minmax(0, 1fr);
        gap: 10px 20px;
        align-items: baseline;
    }

    &__readLabel {
        font-family: "Roboto", sans-serif;
        font-size: 12px;
        font-weight: 500;
        color: var(--eidQualityBlack-75);
        line-height: 1.5;
        padding-top: 1px;
    }

    &__readValue {
        font-family: "Roboto", sans-serif;
        font-size: 14px;
        font-weight: 400;
        color: var(--eidSecureBlue);
        line-height: 1.5;
        word-break: break-word;
        overflow-wrap: anywhere;
        min-width: 0;
    }

    &__readEmpty {
        color: var(--eidQualityBlack-25);
    }

    &__address {
        font-family: "Roboto", sans-serif;
        font-size: 14px;
        font-weight: 400;
        color: var(--eidSecureBlue);
        font-style: normal;
        line-height: 1.7;
        display: flex;
        flex-direction: column;
    }

    // Custom Properties read view — single grid with no sub-sections
    &__readView > &__readGrid {
        padding: 20px 0;
    }

    // -------------------------
    // EDIT MODE FORM
    // -------------------------
    &__form {
        padding: 16px 0 22px;
        font-family: "Roboto", sans-serif;

        & > .JsonSchemaObjectForm {
            margin: 0;
        }

        .JsonSchemaObjectForm__header {
            display: none;
        }

        .JsonSchemaObjectForm__profile {
            margin-top: 0;
        }

        .FormElement {
            display: flex;
            flex-direction: column;
            gap: 8px;
            box-shadow: none;
            background: transparent;
            margin: 0;
            padding: 15px 0 0 0;
        }

        .FormElement:hover {
            background: transparent;
        }

        .FormElement__label {
            display: flex;
            align-items: center;
            gap: 3px;
            font-size: 12px;
            font-weight: 500;
            color: var(--eidSecureBlue);
            padding: 0;
            line-height: 20px;
        }

        .FormElement__requiredIndicator {
            display: inline-flex !important;
            align-items: center;
            color: var(--eidDangerRed);
            font-style: normal;
            font-size: 13px;
            font-weight: 600;
            line-height: 1;
        }

        .FormElement__input {
            display: flex;
            flex-direction: column;
            width: 100%;
            align-items: flex-start;

            input:not(.q-field__input),
            .JsonSchemaForm__valueOnly {
                width: 100%;
                min-width: unset;
                height: 40px;
                border: 1px solid var(--eidQualityBlack-25);
                border-radius: 3px;
                padding: 10px 15px;
                background: var(--eidWhite);
                font-size: 14px !important;
                font-weight: 400;
                color: var(--eidSecureBlue);
                line-height: 20px;
                outline: none;
                align-self: stretch;
            }

            .JsonSchemaForm__valueOnly {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            input:disabled,
            input[disabled] {
                background: var(--eidBgGrayTones-lightest);
                color: var(--eidQualityBlack-25);
                cursor: not-allowed;
                border-color: var(--eidQualityBlack-10);
            }

            .JsonSchemaForm__valueOnly--disabled {
                background: var(--eidBgGrayTones-lightest);
                color: var(--eidQualityBlack-25);
                cursor: not-allowed;
                border-color: var(--eidQualityBlack-10);
            }

            input:focus:not([disabled]):not(.q-field__input) {
                border-color: var(--eidTrustGreen);
                outline: none;
                box-shadow: 0 0 0 2px var(--eidTrustGreen-10);
            }

            // EntityDetails/index.scss sets .FormInput to flex-direction: row (0,2,0 specificity),
            // placing the error beside the input. Nesting here gives 0,3,0 specificity to win.
            .FormInput {
                flex-direction: column;
                align-items: stretch;
                padding-right: 0;
            }
        }

        .FormInput__icon {
            img[alt="lock-icon"],
            .EidIcon img {
                opacity: 0.45;
                filter: invert(40%) sepia(10%) saturate(400%) hue-rotate(180deg);
            }
        }

        .FormInput--with-icon input:disabled,
        .FormInput--with-icon input[disabled] {
            background: var(--eidBgGrayTones-lightest);
            color: var(--eidQualityBlack-25);
            border-color: var(--eidQualityBlack-10);
            cursor: not-allowed;
            padding-right: calc(30px + 1em);

            &:focus {
                border-color: var(--eidQualityBlack-10);
                box-shadow: none;
            }
        }

        .Dropdown__container {
            height: 40px !important;
            padding: 0 15px !important;
            background-color: var(--eidWhite);
            border: 1px solid var(--eidQualityBlack-25);
            border-radius: 3px;
            margin-left: 0;
            width: 100%;
            align-self: stretch;
        }

        // Error borders — ensure all field types show red outline on validation error
        .FormInput--error {
            .Dropdown__container {
                border-color: var(--eidDangerRed) !important;
            }

            .NewDropdown .q-field__control::before {
                border-color: var(--eidDangerRed) !important;
            }
        }

        // Country dropdown clear button — flat icon style matching platform UI.
        // Overrides the grey pill from JsonSchemaForm/index.scss.
        .JsonSchemaCountryEnumForm__clear {
            background: transparent;
            color: var(--eidQualityBlack-50);
            border-radius: 3px;
            padding: 2px 4px;

            &:hover {
                background: var(--eidBgGrayTones-lightest);
                color: var(--eidQualityBlack);
            }
        }

        // Hide "Clear" text in country dropdown — show icon only
        .JsonSchemaCountryEnumForm__clearText {
            display: none;
        }

        .Dropdown__container, .Dropdown__optionsContainer, .NewDropdown {
            width: 100%;
            margin-left: 0;
        }

        .EntityDetailsBooleanForm .NewDropdown {
            max-width: 120px;
        }

        .SingleSelectOptions__options {
            margin: 0;
            padding: 0;
        }

        .SingleSelectOptions__option:hover {
            background-color: var(--eidBgGrayTones-lightest);
            color: var(--eidQualityBlack);
        }

        .JsonSchemaArrayForm__item {
            width: 100%;

            & + .JsonSchemaArrayForm__item {
                margin-top: 8px;
            }

            // Fix error message stacking: EntityDetails/index.scss sets FormInput to
            // flex-direction: row globally, which places the error beside the input.
            // Override back to column so the error message appears below the input.
            .FormInput {
                flex-direction: column;
                align-items: stretch;
            }

            // Fix 1: inner FormElement has padding-top: 15px which stacks on top of
            // the outer FormElement's gap, creating ~23px gap vs ~8px for normal fields.
            // The outer FormElement already owns the label + gap, so inner needs 0.
            .FormElement {
                padding-top: 0;
            }

            // X button sits outside the input as a flex sibling to its right.
            // FormInput__inputRow is already display:flex, so actionContainer
            // naturally appears beside the input container — no absolute positioning needed.
            .FormInput__actionContainer {
                display: flex !important;
                flex-shrink: 0;
                align-self: center;
                opacity: 0;
                transition: opacity 0.15s ease;

                // Override EntityDetails/index.scss which hides .JsonSchemaForm__deleteIcon
                // inside array items. We use opacity on the container instead of display toggling.
                .JsonSchemaForm__deleteIcon {
                    display: flex !important;
                }
            }

            &:hover .FormInput__actionContainer {
                opacity: 1;
            }
        }

        .JsonSchemaArrayForm__addButtonFormInput {
            width: auto !important;
            margin-top: 8px;

            .FormInput__inputRow {
                width: auto;
            }

            .FormInput__actionContainer {
                display: none;
            }
        }

        .JsonSchemaArrayForm__addButton {
            padding-left: 0;
            font-size: 12px;
            font-weight: 500;
        }

        // -------------------------
        // ADDRESS SUB-FORM GRID
        // All fields are full width for consistency with the rest of the form
        // -------------------------
        .JsonSchemaAddressForm {
            width: 100%; // stretch to fill FormElement__input which uses align-items: flex-start

            // Remove the duplicate title — outer FormElement already shows "My Address"
            .JsonSchemaAddressForm__title {
                display: none;
            }

            .JsonSchemaAddressForm__content {
                display: grid;
                grid-template-columns: repeat(12, 1fr);
                column-gap: 12px;
                row-gap: 0;
                padding-left: 12px;
                border-left: 2px solid var(--eidQualityBlack-10);
                margin-top: 4px;

                > * {
                    grid-column: span 12; // all fields full width
                }

                // Override EntityDetailsAddressForm/index.scss which uses a horizontal
                // label-beside-input layout (flex-row, 105px fixed label). We want our
                // vertical label-above-input style to apply in this tab.
                // Higher specificity (4 classes) beats the existing 3-class rule.
                .FormElement {
                    flex-direction: column;
                    padding-left: 0;
                    gap: 6px;
                    width: 100%;
                    padding-top: 10px;
                }

                .FormElement__label {
                    width: auto; // override the fixed 105px
                }

                .FormElement__labelHint {
                    display: flex; // was hidden by EntityDetailsAddressForm CSS
                }

                .FormElement__input {
                    width: 100%;
                    flex-direction: column;
                }

                // Country: give the selected value text enough room so the X
                // button (positioned absolute at right: 25px) never overlaps it.
                /* stylelint-disable-next-line selector-max-class */
                .JsonSchemaCountryEnumForm .Dropdown__valueText {
                    padding-right: 40px;
                }
            }
        }
    }

    // Collaterals — spans both columns
    &__collaterals {
        grid-column: 1 / -1;
    }

    // Equipment — spans both columns
    &__equipment {
        grid-column: 1 / -1;
    }

    // -------------------------
    // RESPONSIVE
    // -------------------------

    @media (max-width: 860px) {
        & {
            grid-template-columns: 1fr;
            padding: 16px 16px 20px;
        }

        &__left,
        &__right,
        &__collaterals,
        &__equipment {
            grid-column: 1;
        }

        &__readGrid {
            grid-template-columns: minmax(0, 130px) minmax(0, 1fr);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, markRaw, PropType } from 'vue';

    import { faPlus } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { Pointer } from '@evidentid/json-schema/FormController';
    import { GovernmentIssuedWcJurisdictions } from '@evidentid/tprm-portal-lib/models/common';
    import { Entity, CustomProperty, EntityAddress } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        CustomPropertyCollateral,
        CustomPropertyEquipment,
    } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';
    import { EntityRiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import UnsavedChangesModal from '@/modules/entity-details/components/UnsavedChangesModal/UnsavedChangesModal.vue';
    import {
        ProducerRow,
        ProducerRowErrors,
    } from '@/modules/entity-details/utils/producer-forms/producerRows';
    import { useWcJurisdictionsStore } from '@/modules/entity-details/wcJurisdictions.store';

    import EntityDetailsCollateralSection from '../EntityDetails/components/EntityDetailsCollateralSection/EntityDetailsCollateralSection.vue';
    import EntityDetailsEquipmentSection from '../EntityDetails/components/EntityDetailsEquipmentSection/EntityDetailsEquipmentSection.vue';

    import EntityDetailsProducersCard from './sub-components/EntityDetailsProducersCard.vue';
    import EntityDetailsPropertiesTabCard from './sub-components/EntityDetailsPropertiesTabCard.vue';
    import EntityDetailsPropertiesTabCustomPropertiesCard from './sub-components/EntityDetailsPropertiesTabCustomPropertiesCard.vue';
    import EntityDetailsPropertiesTabPropertiesCard from './sub-components/EntityDetailsPropertiesTabPropertiesCard.vue';
    import EntityDetailsPropertiesTabToolbar from './sub-components/EntityDetailsPropertiesTabToolbar.vue';
    import EntityDetailsRiskProfilesCard from './sub-components/EntityDetailsRiskProfilesCard.vue';

    /** The part of an Equipment section this tab drives directly. */
    interface EquipmentSectionRef {
        onClickAddEquipment(): void;
    }

    // One shared identity for "no rows yet". A section copies this prop into local state whenever
    // it changes, so handing back a fresh [] on every render would wipe rows as they are added.
    const NO_EQUIPMENT: CustomPropertyEquipment[] = [];

    export default defineComponent({
        name: 'EntityDetailsPropertiesTab',
        components: {
            RbacDisableGuard,
            EidIcon,
            NewButton,
            EntityDetailsCollateralSection,
            EntityDetailsEquipmentSection,
            EntityDetailsPropertiesTabCard,
            EntityDetailsRiskProfilesCard,
            EntityDetailsPropertiesTabToolbar,
            EntityDetailsPropertiesTabPropertiesCard,
            EntityDetailsPropertiesTabCustomPropertiesCard,
            EntityDetailsProducersCard,
            UnsavedChangesModal,
        },
        props: {
            entity: {
                type: Object as PropType<Entity>,
                required: true,
            },
            isEvidentUser: {
                type: Boolean,
                default: false,
            },
            fields: {
                type: Object as PropType<Record<string, Pointer>>,
                required: true,
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
            disableSave: {
                type: Boolean,
                required: true,
            },
            loading: {
                type: Boolean,
                required: true,
            },
            // Keeps the form open so the user can act on the error instead of losing their edits.
            saveFailed: {
                type: Boolean,
                default: false,
            },
            entityRiskProfiles: {
                type: Array as PropType<EntityRiskProfile[]>,
                default: (): EntityRiskProfile[] => [],
            },
            collaterals: {
                type: Array as PropType<CustomPropertyCollateral[]>,
                default: (): CustomPropertyCollateral[] => [],
            },
            customPropertyCollaterals: {
                type: Object as PropType<CustomProperty | null>,
                default: null,
            },
            /** The Equipment custom properties, one section each. */
            customPropertiesEquipment: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            updatingEntity: {
                type: Boolean,
                default: false,
            },
            producerRows: {
                type: Array as PropType<ProducerRow[]>,
                default: () => [],
            },
            producerCoverageTypes: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            /** The entity's address as the open form holds it, for the producer coverage rules. */
            entityAddress: {
                type: Object as PropType<Partial<EntityAddress> | null>,
                default: null,
            },
            // Whether `producerCoverageTypes` reflects a completed load, so an empty list can
            // be read as "this entity has no requirements" rather than "they are not known".
            producerRequirementsLoaded: {
                type: Boolean,
                default: true,
            },
            // The contact email as the form currently holds it. The producer/contact collision
            // rule has to compare against what is being saved, not what was last persisted:
            // the same form edits this field, so the stored value goes stale mid-session.
            entityContactEmail: {
                type: String,
                default: '',
            },
            producerServerErrors: {
                type: Object as PropType<Record<string, ProducerRowErrors>>,
                default: () => ({}),
            },
            // Dirtiness is the parent's to decide: it owns the whole entity payload and is
            // the one that can tell whether it differs from what is stored.
            hasChanges: {
                type: Boolean,
                default: false,
            },
        },
        // PRODUCT-26110 Task 13: this component's template root is RbacDisableGuard,
        // whose render() always returns a Fragment (see RbacDisableGuard.vue's comment).
        // EntityDetails.vue binds every one of these listeners on its single usage; without
        // a declared `emits`, Vue 3 treats them as extraneous and warns when it can't
        // forward them through the Fragment root -- declaring them here marks them
        // consumed at this hop instead.
        emits: [
            'editing-change', 'collaterals-change', 'equipment-change', 'body-change',
            'producers-change', 'save', 'cancel', 'profile-click',
        ],
        data() {
            return {
                RbacFeature,
                faPlus,
                isEditing: false,
                collateralsValid: true,
                producersValid: true,
                // Vue gathers refs declared inside a v-for into one array, so every Equipment
                // section registers itself under its own property key instead — each "Add
                // Equipment" button has to reach the section it sits above. Raw, because these
                // are component instances rather than state to track.
                equipmentSections: markRaw({} as Record<string, EquipmentSectionRef>),
                equipmentValidity: {} as Record<string, boolean>,
                // Bumping this remounts the sections, dropping edits they hold locally.
                equipmentResetToken: 0,
                saveTouched: false,
                showCancelConfirmModal: false,
            };
        },
        computed: {
            insuredFieldsField(): Pointer | null {
                return (this.fields.body as any).properties?.insuredFields || null;
            },
            contactEmail(): string {
                return this.entityContactEmail || this.entity.contactEmail || '';
            },
            wcJurisdictions(): GovernmentIssuedWcJurisdictions | null {
                return useWcJurisdictionsStore().jurisdictions;
            },
            wcJurisdictionsFailed(): boolean {
                return useWcJurisdictionsStore().failed;
            },
            producerFlowEnabled(): boolean {
                return (this as any).$rp.currentDetails?.producerFlowEnabled || false;
            },
            equipmentValid(): boolean {
                // Only properties that still exist may hold back a save. A property removed from
                // configuration would otherwise leave a stale `false` behind and block every other
                // field on the entity with nothing on screen to explain it.
                return this.customPropertiesEquipment
                    .every(({ key }) => this.equipmentValidity[key] !== false);
            },
            /** Whether everything validated on the client is currently satisfied. */
            localValidationPassed(): boolean {
                return !this.disableSave
                    && this.collateralsValid
                    && this.producersValid
                    && this.equipmentValid;
            },
        },
        watch: {
            isEditing(newVal: boolean): void {
                this.$emit('editing-change', newVal);
                // A boot load that gave up gets another go when producers might be edited.
                if (newVal && this.producerFlowEnabled && this.wcJurisdictionsFailed) {
                    this.loadWcJurisdictions();
                }
            },
            loading(newVal: boolean, oldVal: boolean): void {
                // Auto-exit edit mode once a save completes, unless it was refused — that error
                // is about something the user can still fix in the form.
                if (oldVal && !newVal && this.isEditing && !this.saveFailed) {
                    this.isEditing = false;
                    this.collateralsValid = true;
                    this.producersValid = true;
                    this.resetEquipment();
                    this.saveTouched = false;
                }
            },
        },
        methods: {
            loadWcJurisdictions(): void {
                useWcJurisdictionsStore().load();
            },
            onCollateralsInput(collaterals: CustomPropertyCollateral[]): void {
                this.$emit('collaterals-change', collaterals);
            },
            getEquipmentRows(key: string): CustomPropertyEquipment[] {
                return (this.entity.insuredFields?.[key] as CustomPropertyEquipment[]) || NO_EQUIPMENT;
            },
            registerEquipmentSection(key: string, section: unknown): void {
                if (section) {
                    this.equipmentSections[key] = section as EquipmentSectionRef;
                } else {
                    delete this.equipmentSections[key];
                }
            },
            onClickAddEquipment(key: string): void {
                this.equipmentSections[key]?.onClickAddEquipment();
            },
            onEquipmentInput(key: string, rows: CustomPropertyEquipment[]): void {
                this.$emit('equipment-change', { key, rows });
            },
            onEquipmentValidityChange(key: string, valid: boolean): void {
                this.equipmentValidity = { ...this.equipmentValidity, [key]: valid };
            },
            /** Drops the validity collected per section and remounts them to re-read the entity. */
            resetEquipment(): void {
                this.equipmentValidity = {};
                this.equipmentResetToken += 1;
            },
            onProducersChange(rows: ProducerRow[]): void {
                this.$emit('producers-change', rows);
            },
            onBodyChange(): void {
                this.$emit('body-change');
            },
            onCustomPropertiesChange(): void {
                this.$emit('body-change');
            },
            onSaveClick(): void {
                this.saveTouched = true;
                if (!this.hasChanges) {
                    // No changes — exit edit mode without a network request
                    this.cancelEdit();
                    return;
                }
                // Emitted even when local validation has already failed. Producer emails are
                // checked for blocked domains over the network, and those errors land on the same
                // rows as the local ones — so the attempt has to reach the parent for every
                // problem to be reported in one pass. Whether the save itself may proceed is
                // passed along rather than decided here.
                this.$emit('save', this.localValidationPassed);
            },
            onCancelClick(): void {
                if (this.hasChanges) {
                    this.showCancelConfirmModal = true;
                } else {
                    this.cancelEdit();
                }
            },
            onCancelConfirm(confirmed: boolean): void {
                this.showCancelConfirmModal = false;
                if (!confirmed) {
                    // "Discard changes" — exit edit mode and reset
                    this.cancelEdit();
                }
                // confirmed = true means "Return to page" — stay in edit mode
            },
            cancelEdit(): void {
                this.isEditing = false;
                this.collateralsValid = true;
                this.producersValid = true;
                this.resetEquipment();
                this.saveTouched = false;
                this.$emit('cancel');
            },
        },
    });
</script>
